import { ACTION_SHEET_HIDDEN_COLUMNS, HiddenCompositionSharePAs } from '../../state/column-hiding.state';
import { ActionType, useActionState } from '../../../status/state/actions.state';
import { CustomTextField, CustomTextFieldAutoCompl } from '../../../../shared/components/CustomTextField';
import { prefixFirstFilter } from '../../../../shared/utils/autocomplete.utils';
import { DefaultValues, useForm, useWatch } from 'react-hook-form';
import { ModalBody, ModalHeader } from '@ingka/modal';
import { StyledModalFooter, StyledModel, StyledSheetDiv, StyledSheets } from '../../../../shared/styles/components/integrations.styles';
import { addValidationSchema, bulkEditValidationSchema, editValidationSchema } from './add-edit.validation';
import { processBulkEditRow, resolveLatestActive } from './confirm.utils';
import { buildEditDataToSend, toNullableValue } from './confirm.payload.utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useItemMutationWithCacheUpdate, useMutationWithCacheUpdate } from '../../state/table-data.state';

import APIService from '../../../../services/api.service';
import { BulkEditActionSheetPillBar } from '../bulk-edit/BuildEditActionSheetPillBar';
import Button from '@ingka/button';
import { DWPWeightSection } from './DWPWeightSection';
import type { Item } from '../../types/item.types';
import { ItemEditingSection } from './ItemEditingSection';
import { MaterialGradeIdField } from '../../../material-grade/components/MaterialGradeIdField';
import { MaterialGradeModal } from '../../../material-grade/components/MaterialGradeModal';
import { PiaMaterialMappingData } from '../../../material-grade/types/material-mapping.api';
import type { SubItem } from '../../types/sub-item.types';
import { UNASSIGNED_MATERIAL_GRADE_ID } from '../../../material-grade/types/material-grade.constants';
import flatMap from 'lodash/fp/flatMap';
import flow from 'lodash/fp/flow';
import { isUpdatedByOrAt } from '../../../../shared/utils/formatters.utils';
import map from 'lodash/fp/map';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import shallow from 'zustand/shallow';
import styled from '@emotion/styled';
import { filterEmissionColumnsByRole, tableColumns } from '../table/ColumnDefs';
import { tokens } from '../../../../shared/styles/core/tokens';
import uniq from 'lodash/fp/uniq';
import { useAuth } from '../../../auth/state/auth.state';
import { useAutoCompleteSections } from '../../hooks/useAutocomplete';
import { useDropDownDataState } from '../../state/dropdown-data.state';
import { useGetBulkEditItemsFromSelection } from '../bulk-edit/bulk-edit.state';
import { useMaterialGradeSuggestions } from '../../../material-grade/hooks/useMaterialGradeSuggestions';
import { useParentItemsFromSelectedSubItems } from '../../state/selection.state';
import { useTreeData } from '../../hooks/useTreeData';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert } from '../../../../shared/styles/components/feedback.styles';

const isNotEditingItem = (type: ActionType): boolean => {
   switch (type) {
      case ActionType.none:
      case ActionType.adding:
      case ActionType.editing:
      case ActionType.bulkAdding:
      case ActionType.bulkEditing:
         return true;
      case ActionType.editingItem:
         return false;
      default:
         return false;
   }
};

const getResolver = (type: ActionType) => {
   if (!isNotEditingItem(type)) {
      return undefined;
   }
   switch (type) {
      case ActionType.bulkEditing:
         return yupResolver(bulkEditValidationSchema);
      case ActionType.bulkAdding:
      case ActionType.adding:
      case ActionType.none:
         return yupResolver(addValidationSchema);
      case ActionType.editing:
         return yupResolver(editValidationSchema);
      default:
         return yupResolver(addValidationSchema);
   }
};

const normalizeForComparison = (s: string) => s.trim().toLowerCase();

export const ActionSheet = () => {
   const [autoCompleteOpen, setAutoCompleteOpen] = useState(false);

   const [showMaterialGradeModal, setShowMaterialGradeModal] = useState(false);
   const [materialMappingScope, setMaterialMappingScope] = useState<{
      isFamilyNameSpecific: boolean;
      isMaterialPartSpecific: boolean;
      isItemNoSpecific: boolean;
      excludedItemNos?: string[];
   }>({
      isFamilyNameSpecific: true,
      isMaterialPartSpecific: false,
      isItemNoSpecific: false,
      excludedItemNos: [],
   });

   const [actionType, parentItem, singleRowData] = useActionState(
      (state) => [state.actionType, state.parentItem, state.editRowData, state.bulkEditRowData],
      shallow
   );

   const { canEdit, canManageEmissionFactors } = useAuth();
   const parentItems = useParentItemsFromSelectedSubItems(actionType === ActionType.bulkAdding);
   const { bulkEditSubItems, combinedDifferingFields } = useGetBulkEditItemsFromSelection();
   const [partDetails, materialParts] = useDropDownDataState((state) => [state.data.materialTypes, state.data.materialParts]);
   const dropDownData = useDropDownDataState((state) => state.data);

   const isBulk = actionType === ActionType.bulkAdding || actionType === ActionType.bulkEditing;
   const isOpen = actionType !== ActionType.none;

   const parentItemClone = useMemo(() => {
      return parentItem ? structuredClone(parentItem) : null;
   }, [parentItem]);

   const {
      getPartDetailOptions,
      getPartOptions,
      getMaterialGradeOptions,
      handlePartDetailChange,
      handlePartChange,
      handleMaterialGradeChange,
      getFilteredParts,
      getCompatiblePartDetails,
   } = useTreeData();

   const parts = useMemo(
      () =>
         flow(
            flatMap((i: Item) => i?.subItems),
            map((s: SubItem) => s?.part),
            uniq
         )(isBulk ? parentItems : [parentItem!]),
      [isBulk, parentItems, parentItem]
   );

   const usedPartDetails = useMemo(
      () =>
         flow(
            flatMap((i: Item) => i?.subItems),
            map((s: SubItem) => s?.partDetail),
            uniq
         )(isBulk ? parentItems : [parentItem!]),
      [isBulk, parentItems, parentItem]
   );

   const getEmptyFormValues = useCallback(
      (parts: SubItem[]) => ({
         parts,
         part: '',
         materialGrade: '',
         partDetail: '',
         materialGradeId: '',
         threeDModelNo: '',
         volume: '',
         mass: '',
         density: '',
         recycledContent: '',
         renewableContent: '',
         comment: '',
         compositionShare: undefined,
         active: true,
         locked: false,
         clientCreated: true,
         quantity: '1',
      }),
      []
   );

   const getInitialValues = useCallback(() => {
      if (isOpen) {
         switch (actionType) {
            case ActionType.bulkAdding:
               return getEmptyFormValues(parentItems?.flatMap((item) => item.subItems) || []);
            case ActionType.bulkEditing: {
               const base = merge({}, bulkEditSubItems[0], merge({}, ...combinedDifferingFields.map((c) => ({ [c]: '*' }))), {
                  parts: parentItems?.flatMap((item) => item.subItems) || [],
               });
               return {
                  ...base,
                  subItems: bulkEditSubItems.flatMap((s) => s.subItems),
               } as DefaultValues<SubItem>;
            }
            case ActionType.editing:
               return {
                  ...singleRowData,
                  bomValidFrom: singleRowData?.bomValidFrom || parentItem?.bomValidFrom || null,
                  parts: parentItem?.subItems || [],
                  subItems: singleRowData ? [singleRowData] : [],
               } as DefaultValues<SubItem>;
            case ActionType.editingItem:
               return parentItem as DefaultValues<Item>;
            case ActionType.adding:
               return getEmptyFormValues(parentItem?.subItems || []);
            default:
               return {};
         }
      }

      if (!isNotEditingItem(actionType)) {
         return undefined;
      }

      const schemaLookup: Record<ActionType, any> = {
         [ActionType.none]: addValidationSchema,
         [ActionType.adding]: addValidationSchema,
         [ActionType.editing]: addValidationSchema,
         [ActionType.bulkAdding]: addValidationSchema,
         [ActionType.bulkEditing]: bulkEditValidationSchema,
         [ActionType.editingItem]: addValidationSchema,
      };

      return schemaLookup[actionType].cast({});
   }, [actionType, isOpen, parentItems, bulkEditSubItems, combinedDifferingFields, singleRowData, parentItem, getEmptyFormValues]);

   const { control, handleSubmit, reset, trigger, setValue, getValues } = useForm<any>({
      mode: 'all',
      defaultValues: getInitialValues(),
      resolver: getResolver(actionType),
   });

   const watchedMaterialGradeId = useWatch({ control, name: 'materialGradeId' });
   const watchedMaterialGrade = useWatch({ control, name: 'materialGrade' });
   const watchedPart = useWatch({ control, name: 'part' });
   const watchedPartDetail = useWatch({ control, name: 'partDetail' });
   const isAddOperation = actionType === ActionType.adding || actionType === ActionType.bulkAdding;

   const { suggestions: materialGradeSuggestions } = useMaterialGradeSuggestions(isAddOperation ? watchedMaterialGradeId : undefined);

   const { getAutoCompleteSections, getAutoCompleteOptions } = useAutoCompleteSections({
      dropDownData,
      materialParts,
      partDetails,
      usedPartDetails,
      itemStates: [],
      parts,
      currentPart: watchedPart,
      currentPartDetail: watchedPartDetail,
      isBulk,
      getPartDetailOptions,
      getFilteredParts,
      getCompatiblePartDetails,
      getMaterialGradeOptions,
      getPartOptions,
   });

   const handleFieldChange = useCallback(
      (accessor: string, value: string | string[] | null) => {
         switch (accessor) {
            case 'partDetail':
               handlePartDetailChange(value);
               break;
            case 'part':
               handlePartChange(value);
               break;
            case 'materialGrade':
               handleMaterialGradeChange(value, getValues('partDetail'), getValues('part'));
               break;
         }
      },
      [handlePartDetailChange, handlePartChange, handleMaterialGradeChange, getValues]
   );

   const handleMaterialGradeChangeClick = useCallback(() => {
      setShowMaterialGradeModal(true);
   }, []);

   const handleMaterialGradeConfirm = useCallback(
      (data: {
         materialGradeId: string;
         isFamilyNameSpecific: boolean;
         isMaterialPartSpecific: boolean;
         isItemNoSpecific: boolean;
         excludedItemNos?: string[];
      }) => {
         setValue('materialGradeId', data.materialGradeId);
         setMaterialMappingScope({
            isFamilyNameSpecific: data.isFamilyNameSpecific,
            isMaterialPartSpecific: data.isMaterialPartSpecific,
            isItemNoSpecific: data.isItemNoSpecific,
            excludedItemNos: data.excludedItemNos || [],
         });
         setShowMaterialGradeModal(false);
      },
      [setValue]
   );

   const handleMaterialGradeCancel = useCallback(() => {
      setShowMaterialGradeModal(false);
   }, []);

   const getCurrentItemNo = useCallback(() => {
      if (actionType === ActionType.editing || actionType === ActionType.adding) {
         return parentItem?.itemNo || '';
      }
      return parentItems?.[0]?.itemNo || '';
   }, [actionType, parentItem, parentItems]);

   useEffect(() => {
      if (isOpen) {
         reset(getInitialValues());
      }
   }, [isOpen, reset, getInitialValues]);

   const triggerValidation = () => trigger('quantity');

   const getSuccessMessage = () => {
      switch (actionType) {
         case ActionType.bulkAdding:
            return 'Material(s) successfully added to selected items';
         case ActionType.bulkEditing:
            return 'Material(s) successfully updated for selected items';
         case ActionType.editing:
            return 'Material successfully updated';
         case ActionType.editingItem:
            return 'Item successfully updated';
         case ActionType.adding:
            return `Material successfully added to item: ${parentItem?.itemNo}`;
         default:
            return 'Success';
      }
   };

   const mutate = useMutationWithCacheUpdate(getSuccessMessage());
   const mutateItem = useItemMutationWithCacheUpdate(getSuccessMessage());

   const cancel = () => {
      useActionState.setState({ actionType: ActionType.none });
   };

   const keyDownHandler = (event: { key: string; preventDefault: () => void }) => {
      if (!autoCompleteOpen && event.key === 'Enter') {
         event.preventDefault();
         void handleSubmit(actionType !== ActionType.editingItem ? confirm : confirmItem)();
      }
   };

   const materialDiffers = useCallback(() => {
      return combinedDifferingFields.includes('materialGrade');
   }, [combinedDifferingFields]);

   const areAnyBulkEditItemsNotClientCreated = useMemo(() => {
      return bulkEditSubItems.some((s) => !s.clientCreated);
   }, [bulkEditSubItems]);

   const normalizeMaterialGradeId = (data: { materialGradeId?: string }) => {
      if (!data.materialGradeId || data.materialGradeId === '') {
         data.materialGradeId = UNASSIGNED_MATERIAL_GRADE_ID;
      }
   };

   const confirm = async (subItem: SubItem) => {
      if (!parentItems && !parentItem) {
         console.error('No parent items or parent item found');
         return;
      }

      if (isBulk) {
         const subItemWithParts = {
            ...subItem,
            parts: parentItems?.map((item) => {
               if (HiddenCompositionSharePAs.includes(item.pa)) {
                  return item.subItems.map(() => ({ compositionShare: '0' }));
               }
               return item.subItems.map(() => ({ compositionShare: item.subItems[0]?.compositionShare || '' }));
            }),
            bomValidFrom: parentItems?.[0]?.bomValidFrom || null,
         };

         normalizeMaterialGradeId(subItemWithParts);

         const isValid = await trigger();
         if (!isValid) {
            console.error('Form is not valid. Validation errors found.');
            return;
         }

         useActionState.setState({ actionType: ActionType.none });

         if (actionType === ActionType.bulkAdding) {
            subItemWithParts.matSetName = 'User created';
            return mutate(
               parentItems.map((item) => ({
                  ...subItemWithParts,
                  parentId: item.itemNo,
                  bomValidFrom: item.bomValidFrom || subItemWithParts.bomValidFrom,
               }))
            );
         } else if (actionType === ActionType.bulkEditing) {
            const itemPaMap = new Map();
            parentItems?.forEach((item) => {
               itemPaMap.set(item.itemNo, HiddenCompositionSharePAs.includes(item.pa));
            });

            const subItemToParentMap = new Map();
            const subItemToBomValidFromMap = new Map();

            parentItems?.forEach((item) => {
               item.subItems.forEach((subItem) => {
                  subItemToParentMap.set(`${subItem.parentId}:${subItem.id}`, item.itemNo);
                  subItemToBomValidFromMap.set(`${subItem.parentId}:${subItem.id}`, item.bomValidFrom || null);
               });
            });

            return mutate(
               bulkEditSubItems
                  .flatMap((s) => s.subItems)
                  .filter((s) => !s.locked)
                  .map((s) => {
                     let processedData: any = { ...subItemWithParts };

                     const parentId = subItemToParentMap.get(`${s.parentId}:${s.id}`);
                     const isDisallowedPA = parentId && itemPaMap.get(parentId);

                     if (isDisallowedPA) {
                        processedData = omit(processedData, ['compositionShare']);
                     }

                     return processBulkEditRow(s, processedData, combinedDifferingFields, subItemToBomValidFromMap);
                  })
            );
         }
      } else if (actionType === ActionType.adding || actionType === ActionType.editing) {
         if (actionType === ActionType.editing && singleRowData && parentItem) {
            const resolvedActive = resolveLatestActive(singleRowData, subItem.active);
            subItem = { ...subItem, active: resolvedActive };

            const isMaterialGradeChange = singleRowData.materialGradeId !== subItem.materialGradeId;

            if (isMaterialGradeChange && subItem.materialGradeId && subItem.materialGradeId !== UNASSIGNED_MATERIAL_GRADE_ID) {
               const supplierMaterial = [
                  {
                     parentId: parentItem.itemNo,
                     bomValidFrom: parentItem.bomValidFrom || null,
                     matSetName: singleRowData.matSetName,
                     id: singleRowData.id,
                     part: singleRowData.part,
                     materialGrade: singleRowData.materialGrade,
                     partDetail: singleRowData.partDetail,
                     threeDModelNo: singleRowData.threeDModelNo ?? '',
                     active: resolvedActive,
                     locked: singleRowData.locked,
                     clientCreated: singleRowData.clientCreated,
                     newFromPia: singleRowData.newFromPia,
                     comment: toNullableValue(singleRowData.comment),
                     volume: toNullableValue(singleRowData.volume),
                     mass: toNullableValue(singleRowData.mass),
                     quantity: toNullableValue(singleRowData.quantity),
                     materialGradeId: parseInt(subItem.materialGradeId),
                     density: toNullableValue(singleRowData.density),
                     recycledContent: toNullableValue(singleRowData.recycledContent),
                     renewableContent: toNullableValue(singleRowData.renewableContent),
                     compositionShare: singleRowData.compositionShare || 0,
                  },
               ];

               if (materialMappingScope.isItemNoSpecific) {
                  mutate(supplierMaterial);
                  useActionState.setState({ actionType: ActionType.none });
                  return;
               }

               const piaMaterialMapping: PiaMaterialMappingData = {
                  parentId: parentItem.itemNo,
                  materialSetName: singleRowData.matSetName,
                  id: singleRowData.id,
                  materialGradeId: parseInt(subItem.materialGradeId),
                  isFamilyNameSpecific: materialMappingScope.isFamilyNameSpecific,
                  isMaterialPartSpecific: materialMappingScope.isMaterialPartSpecific,
                  isItemNoSpecific: materialMappingScope.isItemNoSpecific,
                  excludedItemNos: materialMappingScope.excludedItemNos || [],
               };

               mutate(supplierMaterial);
               APIService.modifyPiaMaterialMapping(piaMaterialMapping).catch((error) => {
                  console.error('Error updating material grade mapping:', error);
               });
               useActionState.setState({ actionType: ActionType.none });
               return;
            }
         }

         const parts = parentItem!.subItems.map((item) => ({
            compositionShare: item.compositionShare,
         }));

         const cleanSubItem = omit(subItem, ['bomValidFrom']);

         const {
            threeDModelNo: rawThreeDModelNo,
            comment: rawComment,
            recycledContent: rawRecycledContent,
            renewableContent: rawRenewableContent,
            ...cleanSubItemWithoutTransientFields
         } = cleanSubItem;

         const subItemWithParts = {
            ...cleanSubItemWithoutTransientFields,
            parts,
            parentId: parentItem?.itemNo || parentItem?.id!,
         };

         if (parentItem?.isVersionedItem && parentItem.originalItemNo) {
            subItemWithParts.parentId = parentItem.originalItemNo;
         }

         normalizeMaterialGradeId(subItemWithParts);

         const isValid = await trigger();
         if (!isValid) {
            console.error('Form is not valid. Validation errors found.');
            return;
         }

         useActionState.setState({ actionType: ActionType.none });

         if (actionType === ActionType.adding) {
            subItemWithParts.matSetName = 'User created';
            return mutate(subItemWithParts);
         } else if (actionType === ActionType.editing) {
            const dataToSend: any = buildEditDataToSend(
               subItemWithParts,
               rawThreeDModelNo,
               rawComment,
               rawRecycledContent,
               rawRenewableContent
            );

            return mutate([dataToSend]);
         }
      }
   };

   const confirmItem = (item: Item) => {
      useActionState.setState({ actionType: ActionType.none });
      if (actionType === ActionType.editingItem) {
         return mutateItem(item);
      }
   };

   const titleText = useMemo(() => {
      switch (actionType) {
         case ActionType.editing:
            return 'Editing material';
         case ActionType.editingItem:
            return 'DWP weights for item: ' + parentItem?.itemNo;
         case ActionType.adding:
            return 'Adding to item: ' + parentItem?.itemNo;
         case ActionType.bulkAdding:
            return 'Adding material to the following items:';
         case ActionType.bulkEditing:
            return 'Editing material on the following items:';
      }
   }, [actionType, parentItem?.itemNo]);

   const filteredTableColumns = useMemo(() => {
      const roleFilteredColumns = filterEmissionColumnsByRole(tableColumns.expanded, canManageEmissionFactors);
      return roleFilteredColumns.filter((column) => {
         if (column.accessorKey === 'compositionShare' && parentItem && HiddenCompositionSharePAs.includes(parentItem.pa)) {
            return false;
         }
         return true;
      });
   }, [canManageEmissionFactors, parentItem]);

   const customErrorMessages = useMemo(() => {
      const messages: Record<string, string | undefined> = {};

      filteredTableColumns.forEach((column) => {
         const accessor = column.accessorKey as string;

         if (column.accessorKey === 'compositionShare') {
            if (isBulk && parentItems?.some((item) => HiddenCompositionSharePAs.includes(item.pa))) {
               const disallowedItems = parentItems?.filter((item) => HiddenCompositionSharePAs.includes(item.pa)) || [];

               if (disallowedItems.length === 1) {
                  const item = disallowedItems[0];
                  messages[accessor] = `Composition share is not applicable for item ${item.itemNo} with PA: ${item.pa}`;
               } else if (disallowedItems.length > 1 && disallowedItems.length <= 3) {
                  const itemsList = disallowedItems.map((item) => `${item.itemNo} (PA: ${item.pa})`).join(', ');
                  messages[accessor] = `Composition share is not applicable for items: ${itemsList}`;
               } else if (disallowedItems.length > 3) {
                  messages[accessor] = `Composition share is not applicable for ${disallowedItems.length} items in your selection`;
               }
            }
         }

         if (column.accessorKey === 'materialGradeId') {
            if (actionType === ActionType.bulkEditing) {
               messages[accessor] = 'Material Grade ID cannot be changed in bulk operations';
            } else if (materialDiffers()) {
               messages[accessor] = 'Selected materials have different MAM IDs. Material Grade ID cannot be edited.';
            }
         }

         if (actionType === ActionType.bulkEditing) {
            if (column.accessorKey === 'partDetail' || column.accessorKey === 'part' || column.accessorKey === 'materialGrade') {
               if (materialDiffers()) {
                  messages[accessor] = 'Selected materials have different identities. Material identity fields cannot be edited in bulk.';
               } else if (areAnyBulkEditItemsNotClientCreated) {
                  messages[accessor] = 'You can only edit material information for user created rows.';
               }
            }

            if (
               combinedDifferingFields.includes(accessor) &&
               accessor !== 'materialGradeId' &&
               accessor !== 'materialGrade' &&
               accessor !== 'part' &&
               accessor !== 'partDetail'
            ) {
               messages[accessor] = 'Different values in selection. Editing will apply the same value to all rows.';
            }
         }
      });

      return messages;
   }, [
      filteredTableColumns,
      actionType,
      isBulk,
      parentItems,
      areAnyBulkEditItemsNotClientCreated,
      combinedDifferingFields,
      materialDiffers,
   ]);

   const disabledStates = useMemo(() => {
      const states: Record<string, boolean> = {};

      filteredTableColumns.forEach((column) => {
         const accessor = column.accessorKey as string;

         if (column.accessorKey === 'compositionShare') {
            if (isBulk) {
               states[accessor] = parentItems?.some((item) => HiddenCompositionSharePAs.includes(item.pa)) || false;
            } else {
               states[accessor] = parentItem && HiddenCompositionSharePAs.includes(parentItem.pa) ? true : false;
            }
         } else if (column.accessorKey === 'id') {
            states[accessor] = !canEdit || materialDiffers();
         } else if (column.accessorKey === 'materialGradeId') {
            states[accessor] = actionType === ActionType.bulkEditing || !canEdit || materialDiffers();
         } else if (column.accessorKey === 'partDetail' || column.accessorKey === 'part' || column.accessorKey === 'materialGrade') {
            if (actionType === ActionType.editing) {
               states[accessor] = singleRowData?.clientCreated ? false : true;
            } else if (actionType === ActionType.bulkEditing) {
               states[accessor] = materialDiffers() || areAnyBulkEditItemsNotClientCreated;
            } else {
               states[accessor] = false;
            }
         } else {
            states[accessor] = false;
         }
      });

      return states;
   }, [
      filteredTableColumns,
      actionType,
      isBulk,
      parentItems,
      parentItem,
      canEdit,
      materialDiffers,
      singleRowData?.clientCreated,
      areAnyBulkEditItemsNotClientCreated,
   ]);

   return (
      <>
         <StyledModel
            visible={isOpen && !showMaterialGradeModal}
            handleCloseBtn={cancel}
            escapable={false}
            onKeyDown={keyDownHandler}
            keepContentMounted={true}
         >
            <StyledSheets
               alignment={'right'}
               preserveAlignment={true}
               size={isBulk || (actionType === ActionType.editing && singleRowData?.suppliers) ? 'medium' : 'small'}
               header={<ModalHeader title={titleText} floating={false} />}
               footer={
                  <StyledModalFooter compact={false}>
                     <Button
                        text={actionType === ActionType.adding ? 'Add ' : 'Save'}
                        type="primary"
                        onClick={() => {
                           const handler = actionType !== ActionType.editingItem ? confirm : confirmItem;
                           handleSubmit(handler)();
                        }}
                     />
                  </StyledModalFooter>
               }
            >
               <ModalBody>
                  <StyledSheetDiv>
                     {(actionType === ActionType.bulkAdding || actionType === ActionType.bulkEditing) && <BulkEditActionSheetPillBar />}
                     {actionType === ActionType.editing && parentItem?.parentItemNo && (
                        <Alert variant="warning" style={{ marginTop: `-${tokens.space.md}` }}>
                           NB! Changes to a connected article might be overwritten
                        </Alert>
                     )}
                     {actionType !== ActionType.editingItem ? (
                        <StyledFlexWrapper>
                           <StyledEditingMaterialContainer>
                              {(() => {
                                 const suggestionCategories = (() => {
                                    if (!isAddOperation || materialGradeSuggestions.length === 0) {
                                       return null;
                                    }

                                    const partHierarchyOptions = getAutoCompleteOptions('materialGrade');

                                    const partHierarchyNormalized = new Set(partHierarchyOptions.map(normalizeForComparison));
                                    const suggestionsNormalized = new Set(materialGradeSuggestions.map(normalizeForComparison));

                                    const bestMatches: string[] = [];
                                    const moreMatches: string[] = [];
                                    for (const suggestion of materialGradeSuggestions) {
                                       if (partHierarchyNormalized.has(normalizeForComparison(suggestion))) {
                                          bestMatches.push(suggestion);
                                       } else {
                                          moreMatches.push(suggestion);
                                       }
                                    }

                                    const partHierarchyOnly = partHierarchyOptions.filter(
                                       (option) => !suggestionsNormalized.has(normalizeForComparison(option))
                                    );

                                    const partHierarchyOnlyNormalized = new Set(partHierarchyOnly.map(normalizeForComparison));

                                    return {
                                       bestMatches,
                                       moreMatches,
                                       partHierarchyOnly,
                                       partHierarchyOnlyNormalized,
                                    };
                                 })();

                                 const getOptionsForField = (field: string) => {
                                    if (field === 'materialGrade' && suggestionCategories) {
                                       const { bestMatches, moreMatches, partHierarchyOnly } = suggestionCategories;
                                       return [...bestMatches, ...moreMatches, ...partHierarchyOnly];
                                    }
                                    return getAutoCompleteOptions(field);
                                 };

                                 const getSectionsForField = (field: string) => {
                                    const baseSections = getAutoCompleteSections(field);
                                    if (field === 'materialGrade' && suggestionCategories) {
                                       const { bestMatches, moreMatches, partHierarchyOnlyNormalized } = suggestionCategories;

                                       const sections = [];

                                       if (watchedPart) {
                                          if (bestMatches.length > 0) {
                                             const truncatedPart = watchedPart.length > 20 ? `${watchedPart.slice(0, 20)}...` : watchedPart;
                                             sections.push({
                                                name: `Matches for ${watchedMaterialGradeId} and ${truncatedPart}`,
                                                values: bestMatches,
                                             });
                                          }
                                          if (moreMatches.length > 0) {
                                             sections.push({
                                                name: `Suggestions for ${watchedMaterialGradeId}`,
                                                values: moreMatches,
                                             });
                                          }
                                       } else {
                                          const allSuggestions = [...bestMatches, ...moreMatches];
                                          if (allSuggestions.length > 0) {
                                             sections.push({
                                                name: `Suggestions for ${watchedMaterialGradeId}`,
                                                values: allSuggestions,
                                             });
                                          }
                                       }

                                       const filteredBaseSections = baseSections
                                          .map((section) => ({
                                             ...section,
                                             values: section.values.filter((v: string) =>
                                                partHierarchyOnlyNormalized.has(normalizeForComparison(v))
                                             ),
                                          }))
                                          .filter((section) => section.values.length > 0);

                                       return [...sections, ...filteredBaseSections];
                                    }
                                    return baseSections;
                                 };

                                 return filteredTableColumns.map((column) => {
                                    const accessor = column.accessorKey as string;

                                    return (
                                       !isUpdatedByOrAt(accessor) &&
                                       !ACTION_SHEET_HIDDEN_COLUMNS.includes(accessor) && (
                                          <StyledSheetDiv key={accessor}>
                                             {accessor === 'partDetail' || accessor === 'part' || accessor === 'materialGrade' ? (
                                                <CustomTextFieldAutoCompl
                                                   autoCompleteOptions={getOptionsForField(accessor)}
                                                   autoCompleteSections={getSectionsForField(accessor)}
                                                   field={accessor}
                                                   label={column.header as string}
                                                   customErrorMessage={customErrorMessages[accessor]}
                                                   disabled={disabledStates[accessor]}
                                                   control={control}
                                                   multiple={false}
                                                   freeSolo={true}
                                                   openState={setAutoCompleteOpen}
                                                   filterOptions={prefixFirstFilter}
                                                   onChange={(value) => handleFieldChange(accessor, value)}
                                                   noOptionsText={
                                                      disabledStates[accessor]
                                                         ? ''
                                                         : accessor === 'materialGrade'
                                                         ? ''
                                                         : getValues(accessor)
                                                         ? 'No other matches found'
                                                         : 'No matches. Be careful when adding new materials.'
                                                   }
                                                />
                                             ) : accessor === 'materialGradeId' ? (
                                                actionType === ActionType.adding || actionType === ActionType.bulkAdding ? (
                                                   <CustomTextField
                                                      field={accessor}
                                                      label={column.header as string}
                                                      customErrorMessage={customErrorMessages[accessor]}
                                                      disabled={disabledStates[accessor]}
                                                      control={control}
                                                   />
                                                ) : (
                                                   <MaterialGradeIdField
                                                      control={control}
                                                      label={column.header as string}
                                                      disabled={disabledStates[accessor]}
                                                      customErrorMessage={customErrorMessages[accessor]}
                                                      onChangeClick={handleMaterialGradeChangeClick}
                                                   />
                                                )
                                             ) : (
                                                <CustomTextField
                                                   field={accessor}
                                                   label={column.header as string}
                                                   customErrorMessage={customErrorMessages[accessor]}
                                                   disabled={disabledStates[accessor]}
                                                   control={control}
                                                   triggerValidation={
                                                      accessor === 'mass' || accessor === 'volume' ? triggerValidation : undefined
                                                   }
                                                />
                                             )}
                                          </StyledSheetDiv>
                                       )
                                    );
                                 });
                              })()}
                           </StyledEditingMaterialContainer>
                           {actionType === ActionType.editing && singleRowData?.suppliers && singleRowData.suppliers.length > 1 && (
                              <DWPWeightSection suppliers={singleRowData.suppliers} control={control} />
                           )}
                        </StyledFlexWrapper>
                     ) : (
                        parentItemClone?.dwpActCpInfo?.suppliers &&
                        parentItemClone && <ItemEditingSection parentItemClone={parentItemClone} control={control} />
                     )}
                  </StyledSheetDiv>
               </ModalBody>
            </StyledSheets>
         </StyledModel>
         <MaterialGradeModal
            visible={showMaterialGradeModal}
            onClose={handleMaterialGradeCancel}
            onConfirm={handleMaterialGradeConfirm}
            itemNo={getCurrentItemNo()}
            familyName={parentItem?.familyName || parentItems?.[0]?.familyName || ''}
            materialPart={watchedPart || ''}
            materialGrade={watchedMaterialGrade || ''}
            currentMaterialGradeId={singleRowData?.materialGradeId}
         />
      </>
   );
};

const StyledFlexWrapper = styled.div`
   display: flex;
   gap: ${tokens.space.xl};
`;

const sharedFlexItemStyles = `
  flex: 1;
  min-width: 24em;
`;

const StyledEditingMaterialContainer = styled.div`
   ${sharedFlexItemStyles}
`;
